import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import {
  REPORT_COLUMN_ANALYSIS_SUMMARY_KEY,
  REPORT_COLUMNS,
  REPORT_COLUMNS_STORAGE_KEY,
} from '@features/Admin/Campaign/constants/reportColumns.constants';
import {
  buildReportQueryParameters,
  getInitialAdditionalColumns,
  getStoredAdditionalColumns,
  getToggledAdditionalColumns,
  saveSelectedAdditionalColumns,
  transformToDDMMYYYY,
} from '@features/Admin/Campaign/utils/ongoing-calls.utils';
import { useLazyGetCampaignReportQuery } from '@shared/store/api/baseApi';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';

interface UseOngoingCallsReportOptions {
  campaignId: string | null;
  filterFormData: CallsFilterFormValue;
}

export const useOngoingCallsReport = ({
  campaignId,
  filterFormData,
}: UseOngoingCallsReportOptions) => {
  const { t } = useTranslation();
  const [showReportColumnsDialog, setShowReportColumnsDialog] = useState(false);
  const [selectedAdditionalColumns, setSelectedAdditionalColumns] = useState<string[]>([]);
  const [separatedAnalysisSummary, setSeparatedAnalysisSummary] = useState(false);
  const reportColumnsInitializedRef = useRef(false);

  const [getCampaignReport] = useLazyGetCampaignReportQuery();

  const downloadReport = useCallback(
    async (selectedColumns: string[] = [], separatedAnalysisSummaryParam?: boolean) => {
      if (!campaignId) return;
      try {
        const queryParams = buildReportQueryParameters(filterFormData, selectedColumns, {
          includeAdditionalColumns: true,
          separatedAnalysisSummary:
            separatedAnalysisSummaryParam === true &&
            selectedColumns.includes(REPORT_COLUMN_ANALYSIS_SUMMARY_KEY),
        });
        const result = await getCampaignReport({ campaignId, queryParams });

        if (result.error) {
          toast.error('Cannot download Report');
          return;
        }

        const { blob, contentDisposition } = result.data!;
        const url = window.URL.createObjectURL(blob);

        const fileNameMatch = contentDisposition?.match(/filename="?(.+)"?/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : `call-report-${transformToDDMMYYYY(Date.now().toString())}.xlsx`;

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        toast.success(CustomSuccessToast, {
          closeButton: false,
          autoClose: 5000,
          icon: false,
          data: {
            title: t('campaignPage.downloadReportStarted.title'),
            description: t('campaignPage.downloadReportStarted.description'),
          },
        });
      } catch {
        toast.error('Cannot download Report');
      }
    },
    [campaignId, filterFormData, getCampaignReport, t],
  );

  useEffect(() => {
    if (!showReportColumnsDialog || reportColumnsInitializedRef.current || !REPORT_COLUMNS.length)
      return;
    const storedColumns = getStoredAdditionalColumns(REPORT_COLUMNS_STORAGE_KEY);
    const initialColumns = getInitialAdditionalColumns(REPORT_COLUMNS, storedColumns);

    setSelectedAdditionalColumns(initialColumns);
    saveSelectedAdditionalColumns(REPORT_COLUMNS_STORAGE_KEY, initialColumns);
    reportColumnsInitializedRef.current = true;
  }, [showReportColumnsDialog]);

  const handleReportColumnsDialogOpen = useCallback(() => {
    setShowReportColumnsDialog(true);
  }, []);

  const handleReportColumnsDialogClose = useCallback(() => {
    setShowReportColumnsDialog(false);
  }, []);

  const handleAdditionalColumnToggle = useCallback((columnKey: string) => {
    setSelectedAdditionalColumns((prevState) => {
      const nextState = getToggledAdditionalColumns(prevState, columnKey);
      if (
        columnKey === REPORT_COLUMN_ANALYSIS_SUMMARY_KEY &&
        !nextState.includes(REPORT_COLUMN_ANALYSIS_SUMMARY_KEY)
      ) {
        setSeparatedAnalysisSummary(false);
      }
      saveSelectedAdditionalColumns(REPORT_COLUMNS_STORAGE_KEY, nextState);
      return nextState;
    });
  }, []);

  const handleExportWithColumns = useCallback(async () => {
    await downloadReport(selectedAdditionalColumns, separatedAnalysisSummary);
    handleReportColumnsDialogClose();
  }, [
    downloadReport,
    handleReportColumnsDialogClose,
    selectedAdditionalColumns,
    separatedAnalysisSummary,
  ]);

  return {
    showReportColumnsDialog,
    selectedAdditionalColumns,
    separatedAnalysisSummary,
    setSeparatedAnalysisSummary,
    handleReportColumnsDialogOpen,
    handleReportColumnsDialogClose,
    handleAdditionalColumnToggle,
    handleExportWithColumns,
  };
};
